import React from "react";
import { GovShareButton } from "@/components/governance/GovShare";
import { Badge } from "@/components/primitives";

const statusTone = (s) => (s === "registered" ? "success" : s === "special" ? "info" : s === "retired" ? "default" : "default");

const adaShort = (lovelace) => {
  const n = Number(lovelace) / 1e6;
  if (!isFinite(n)) return "—";
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B ₳`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M ₳`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K ₳`;
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} ₳`;
};

// Rich DRep sharing — supplies the DRep-specific preview card (identity +
// voting power + voting record) and the backend share path.
export const DrepShare = ({ drepId, d }) => {
  const name = (d?.metadata?.given_name || d?.given_name || "").trim() || "Delegated Representative";
  const status = d?.status || "—";
  const v = d?.votes || {};
  const y = Number(v.yes) || 0;
  const n = Number(v.no) || 0;
  const a = Number(v.abstain) || 0;
  const tot = y + n + a;
  const pct = (x) => (tot > 0 ? `${((x / tot) * 100).toFixed(0)}%` : "0%");

  return (
    <GovShareButton
      tid="drep"
      heading="Share this DRep"
      sharePath={`governance/drep/${drepId}`}
      title={name}
      tweetText={`${name} · Cardano DRep on Ada Explorer`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-[9px] uppercase tracking-[0.25em] mono text-[#22d3ee]">ada explorer · governance</span>
        <Badge tone={statusTone(status)}>{status}</Badge>
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] mono text-[#22d3ee]/80">DRep</div>
      <div className="text-[18px] font-heading font-semibold text-foreground leading-snug break-words">{name}</div>
      <div className="text-[10px] mono text-muted-foreground/70 break-all">{drepId}</div>
      <div className="grid grid-cols-2 gap-3 pt-1">
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] mono text-muted-foreground">Voting power</div>
          <div className="text-[13px] mono text-foreground truncate">{d?.voting_power ? adaShort(d.voting_power) : "—"}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] mono text-muted-foreground">Delegators</div>
          <div className="text-[13px] mono text-foreground">{d?.delegators_count ?? "—"}</div>
        </div>
      </div>
      <div className="pt-1 space-y-1.5">
        <div className="text-[9px] uppercase tracking-[0.2em] mono text-muted-foreground">Voting record</div>
        <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted/20">
          <div className="bg-emerald-500" style={{ width: pct(y) }} />
          <div className="bg-red-500" style={{ width: pct(n) }} />
          <div className="bg-muted-foreground/40" style={{ width: pct(a) }} />
        </div>
        <div className="text-[10px] mono text-muted-foreground">Yes {y} · No {n} · Abstain {a}</div>
      </div>
    </GovShareButton>
  );
};
